/**
 * `BriefDetailError` — error state rendered when the brief query rejects.
 *
 * Pure presentational. Mirrors the briefs-list `BriefsErrorState` pattern
 * (monks-ui `<Empty>` + retry button). The page wires `onRetry` to
 * `query.refetch()`.
 */
import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@monksflow/monks-ui';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BriefDetailErrorProps {
  /** Fires when the user clicks "Try again". Wired by the parent to `query.refetch()`. */
  onRetry: () => void;
}

export function BriefDetailError(props: BriefDetailErrorProps) {
  const { t } = useTranslation('projects');

  return (
    <Empty role="alert" data-testid="brief-detail-error">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <AlertCircle className="size-6 text-destructive" />
        </EmptyMedia>
        <EmptyTitle>{t('projectBriefDetail.briefDetailError.couldnTLoadBrief')}</EmptyTitle>
        <EmptyDescription>
          {t('projectBriefDetail.briefDetailError.somethingWentWrongTryAgain')}
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button variant="outline" size="default" onClick={props.onRetry}>
          {t('projectBriefDetail.briefDetailError.tryAgain')}
        </Button>
      </EmptyContent>
    </Empty>
  );
}
